/**
 * Flow editor — synthesised audio: hit sounds per lane + scroll tick.
 *
 * Uses the Web Audio API to generate short blips on the fly so we don't
 * ship any audio asset. The four lanes form a Do-Re-Mi-Fa scale (C/D/E/F
 * in the 5th octave) on the same triangle timbre, so a run of notes
 * across the lanes plays like an ascending melodic line instead of four
 * unrelated timbres:
 *
 *   Lane 0 (green)  — Do (C5, 523.25 Hz)
 *   Lane 1 (red)    — Re (D5, 587.33 Hz)
 *   Lane 2 (yellow) — Mi (E5, 659.25 Hz)
 *   Lane 3 (blue)   — Fa (F5, 698.46 Hz)
 *
 * Borrowed from the SR tickSound module pattern (one shared AudioContext,
 * lazy-warm on first user gesture). We re-use the SR AudioContext via
 * `playTick` exports when needed.
 */

import { probe, setProbeClock } from './flowSyncProbe';
import { ensureHitSample, playHitSample, type HitSampleName } from '../../audio/hitSample';

let audioCtx: AudioContext | null = null;
let enabled = true;
let volume = 0.35;                 // master SFX volume (mixer "SFX / Ticks")

// ── SFX preferences (set from the editor's SFX settings) ──────────────────────
export type ScrollSound = 'classic' | 'click' | 'soft';
let scrollVariant: ScrollSound = 'classic';
let scrollVol = 1;                 // 0..1, multiplies the master for scroll ticks
let noteVol = 1;                   // 0..1, multiplies the master for note hits
let laneUniform = false;           // true → one sound for all lanes (vs 4 per-lane)

export function setScrollSound(s: ScrollSound): void { scrollVariant = s; }
export function setScrollVolume(v: number): void { scrollVol = Math.max(0, Math.min(1, v)); }
export function setNoteVolume(v: number): void { noteVol = Math.max(0, Math.min(1, v)); }
export function setLaneSoundUniform(on: boolean): void { laneUniform = on; }

/** Which VOICE the note hit uses: the synthesised tone (per-lane pitches) or a
 *  recorded sample. 'clap' is the Synths Riders desktop-editor hit sound. */
export type NoteSound = 'tone' | 'clap';
let noteVariant: NoteSound = 'tone';
export function setNoteSound(s: NoteSound): void {
  noteVariant = s;
  // Warm the decode as soon as the voice is picked so the first hit isn't the
  // one that falls back to the tone.
  if (s !== 'tone') void ensureHitSample(getContext(), s as HitSampleName);
}

// Place / delete action SFX (chosen in Settings → Sound Effects).
export type PlaceSound = 'pop' | 'blip' | 'thock' | 'quack' | 'none';
export type DeleteSound = 'shatter' | 'zap' | 'thud' | 'none';
let placeVariant: PlaceSound = 'pop';
let deleteVariant: DeleteSound = 'shatter';
let placeVol = 1;                  // 0..1, own volume (independent of note hits)
let deleteVol = 1;
export function setPlaceSound(s: PlaceSound): void { placeVariant = s; }
export function setDeleteSound(s: DeleteSound): void { deleteVariant = s; }
export function setPlaceVolume(v: number): void { placeVol = Math.max(0, Math.min(1, v)); }
export function setDeleteVolume(v: number): void { deleteVol = Math.max(0, Math.min(1, v)); }

// Feed the sync probe the DSP clock. Lazy closure → no AudioContext is created
// until something actually reads ctx.currentTime (after a user gesture).
setProbeClock(() => getContext().currentTime);

function getContext(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AudioCtor: typeof AudioContext = (window as any).AudioContext
      || (window as any).webkitAudioContext;
    audioCtx = new AudioCtor();
  }
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

/**
 * Shared AudioContext for the Flow editor. Exposed so the music playback
 * engine (flowAudioEngine) drives the song through the SAME clock the hit
 * sounds use — guaranteeing notes, hits and music all share one sample-accurate
 * timeline (AudioContext.currentTime), the rhythm-game standard.
 */
export function getFlowAudioContext(): AudioContext {
  return getContext();
}

interface LaneSoundDef {
  /** Pitch in Hz. */
  frequency: number;
  /** Display name (used only for logs / future tooltips). */
  name: string;
}

// C5, D5, E5, F5, G5 — the first five scale degrees of C major. Same timbre
// across all lanes so multi-lane runs sound like a tonal phrase.
const LANE_SOUNDS: Record<number, LaneSoundDef> = {
  0: { frequency: 523.25, name: 'Do' },  // green
  1: { frequency: 587.33, name: 'Re' },  // red
  2: { frequency: 659.25, name: 'Mi' },  // yellow
  3: { frequency: 698.46, name: 'Fa' },  // blue
  4: { frequency: 783.99, name: 'Sol' }, // purple (5-lane)
};

/** Single envelope shared across all lane hits so they read as one
 *  instrument changing pitch — not four different instruments. */
const HIT_DECAY = 0.28;
const HIT_WAVEFORM: OscillatorType = 'triangle';

/**
 * Play the hit sound for a note landing on `lane`. Lazily inits audio.
 * `meta` is optional instrumentation: the note's beat, the AudioContext time
 * the note's audio is actually due (`expectedCtx`), and a source tag — used by
 * the sync probe to report how far the SFX fired from the music (driftMs).
 */
export function playLaneHit(
  lane: number,
  meta?: { beat?: number; expectedCtx?: number; src?: string },
): void {
  if (!enabled) return;
  const def = laneUniform ? LANE_SOUNDS[0] : LANE_SOUNDS[lane];
  if (!def) return;
  try {
    const ctx = getContext();
    const now = ctx.currentTime;
    probe('hit', {
      lane,
      now,
      ...(meta?.beat !== undefined ? { beat: meta.beat } : {}),
      ...(meta?.expectedCtx !== undefined
        ? { expectedCtx: meta.expectedCtx, driftMs: (now - meta.expectedCtx) * 1000 }
        : {}),
      src: meta?.src ?? 'unknown',
    });
    emitLaneHit(ctx, def, now);
  } catch { /* audio disabled / blocked */ }
}

/**
 * Schedule a lane hit at an EXACT AudioContext time (`when`) — the same
 * look-ahead technique as the metronome (scheduleTickAt). This commits the SFX
 * to the audio thread ahead of time so it lands sample-accurately with the
 * music, instead of being fired reactively from the frame loop (which lagged
 * the beat by ~20–40ms with frame jitter).
 */
export function scheduleLaneHitAt(
  lane: number,
  when: number,
  meta?: { beat?: number; src?: string },
): void {
  if (!enabled) return;
  const def = laneUniform ? LANE_SOUNDS[0] : LANE_SOUNDS[lane];
  if (!def) return;
  try {
    const ctx = getContext();
    const t = Math.max(when, ctx.currentTime + 0.0005); // never schedule in the past
    probe('hit-sched', {
      lane,
      when,
      t,
      leadMs: (t - ctx.currentTime) * 1000,
      ...(meta?.beat !== undefined ? { beat: meta.beat } : {}),
      src: meta?.src ?? 'sched',
    });
    emitLaneHit(ctx, def, t);
  } catch { /* audio blocked */ }
}

/** Build the two-oscillator lane-hit voice starting at AudioContext time `at`.
 *  Shared by the play-now (playLaneHit) and schedule-ahead (scheduleLaneHitAt)
 *  paths so both sound identical. */
function emitLaneHit(ctx: AudioContext, def: LaneSoundDef, at: number): void {
  // Sampled voice (e.g. the desktop editor's clap). Falls through to the
  // synthesised tone below while the sample is still decoding, so a hit is
  // never dropped and never waits.
  if (noteVariant !== 'tone'
    && playHitSample(ctx, noteVariant as HitSampleName, at, volume * noteVol)) {
    return;
  }

  // Main tone — triangle for warm-but-clear musical timbre, rolled off with a
  // gentle low-pass so the attack never bites (user feedback: softer EQ).
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(def.frequency * 4, at);   // keep body, shave fizz
  lp.Q.setValueAtTime(0.5, at);
  osc.type = HIT_WAVEFORM;
  osc.frequency.setValueAtTime(def.frequency, at);
  // Very gentle pitch settle — keeps notes feeling alive without
  // turning them into "plucks" that drop into the bass.
  osc.frequency.exponentialRampToValueAtTime(def.frequency * 0.96, at + HIT_DECAY);
  const noteGain = Math.max(0.0001, volume * noteVol);
  gain.gain.setValueAtTime(0.0001, at);
  // Soft attack and slower release — closer to a mallet hit than a sharp blip.
  gain.gain.exponentialRampToValueAtTime(noteGain, at + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + HIT_DECAY);
  osc.connect(lp);
  lp.connect(gain);
  gain.connect(ctx.destination);
  osc.start(at);
  osc.stop(at + HIT_DECAY + 0.05);

  // Quiet sub-octave to add body without changing the perceived pitch.
  const sub = ctx.createOscillator();
  const subGain = ctx.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(def.frequency / 2, at);
  subGain.gain.setValueAtTime(0.0001, at);
  subGain.gain.exponentialRampToValueAtTime(noteGain * 0.25, at + 0.012);
  subGain.gain.exponentialRampToValueAtTime(0.0001, at + HIT_DECAY);
  sub.connect(subGain);
  subGain.connect(ctx.destination);
  sub.start(at);
  sub.stop(at + HIT_DECAY + 0.05);
}

/** Short click used when scrubbing with the mouse wheel. Three voices:
 *  'classic' = the original 2400→1200 sweep; 'click' = a dry single-freq blip;
 *  'soft' = a mellow low sine. Level = master · scrollVolume. */
export function playScrollTick(): void {
  if (!enabled) return;
  const lvl = volume * scrollVol;
  if (lvl <= 0) return;
  try {
    const ctx = getContext();
    const now = ctx.currentTime;
    probe('scroll-tick', { now });
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (scrollVariant === 'click') {
      // Dry, simple: one short square-ish blip, no pitch sweep.
      osc.type = 'square';
      osc.frequency.setValueAtTime(1500, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(lvl * 0.32, now + 0.002);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
      osc.start(now); osc.stop(now + 0.05);
    } else if (scrollVariant === 'soft') {
      // Mellow low sine — gentle on the ears for fast scrubbing.
      osc.type = 'sine';
      osc.frequency.setValueAtTime(660, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(lvl * 0.4, now + 0.006);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
      osc.start(now); osc.stop(now + 0.1);
    } else {
      // 'classic' — the original metronome-style sweep.
      osc.type = 'sine';
      osc.frequency.setValueAtTime(2400, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.04);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(lvl * 0.45, now + 0.003);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
      osc.start(now); osc.stop(now + 0.07);
    }
  } catch { /* audio blocked */ }
}

/**
 * Schedule a metronome click at an EXACT AudioContext time (`when`), instead
 * of "play now". This is what keeps the metronome sample-accurate: the click
 * is committed to the audio thread ahead of time, immune to frame-loop jitter.
 * `accent` (downbeat) plays a higher, slightly louder click.
 */
export function scheduleTickAt(when: number, accent = false): void {
  if (!enabled) return;
  try {
    const ctx = getContext();
    const t = Math.max(when, ctx.currentTime + 0.0005); // never schedule in the past
    probe('metro-tick', { whenReq: when, t, leadMs: (t - ctx.currentTime) * 1000, accent, clamped: t !== when });
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    // Softer voicing (user feedback: the metronome was harsh on the ears) —
    // lower pitch sweep and gentler levels than the original 3200/2400 blip.
    const hi = accent ? 2200 : 1700;
    const lo = accent ? 1100 : 850;
    osc.frequency.setValueAtTime(hi, t);
    osc.frequency.exponentialRampToValueAtTime(lo, t + 0.04);

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(volume * (accent ? 0.42 : 0.28), t + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.08);
  } catch { /* audio blocked */ }
}

// ── Place / delete action sounds ─────────────────────────────────────────────

/** A short filtered white-noise burst (used by the "shatter" delete voice). */
function emitNoise(ctx: AudioContext, at: number, dur: number, lvl: number,
                   type: BiquadFilterType, f0: number, f1: number): void {
  const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource(); src.buffer = buf;
  const filt = ctx.createBiquadFilter();
  filt.type = type;
  filt.frequency.setValueAtTime(f0, at);
  filt.frequency.exponentialRampToValueAtTime(Math.max(40, f1), at + dur);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(Math.max(0.0001, lvl), at);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  src.connect(filt); filt.connect(gain); gain.connect(ctx.destination);
  src.start(at); src.stop(at + dur + 0.02);
}

function tone(ctx: AudioContext, at: number, type: OscillatorType,
              f0: number, f1: number, lvl: number, dur: number, attack = 0.004): void {
  const osc = ctx.createOscillator(); const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, at);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), at + dur);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, lvl), at + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(gain); gain.connect(ctx.destination);
  osc.start(at); osc.stop(at + dur + 0.03);
}

// Each successive note placement raises the place-sound pitch by 0.1× (a
// climbing-scale feel), stepping through PLACE_PITCH_STEPS values and then
// repeating from the base. Independent of the chosen place-sound variant.
const PLACE_PITCH_STEP = 0.1;
const PLACE_PITCH_STEPS = 10;
const PLACE_PITCH_IDLE_RESET_MS = 10_000; // restart the ramp after a 10s pause
let placePitchStep = 0;
let lastPlaceAt = 0;                        // performance.now() of the last placement

/**
 * A short duck "quack": a sawtooth with a quick up-then-down pitch contour
 * pushed through a nasal band-pass. `pitch` scales the whole thing so it
 * rides the same place-sound pitch ramp as the other voices.
 */
function quack(ctx: AudioContext, at: number, lvl: number, pitch: number): void {
  const dur = 0.17;
  const f = (x: number) => x * pitch;
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(f(430), at);
  osc.frequency.linearRampToValueAtTime(f(640), at + 0.04);
  osc.frequency.linearRampToValueAtTime(f(240), at + dur);
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(f(950), at);
  bp.Q.setValueAtTime(6, at);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, lvl), at + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(bp); bp.connect(gain); gain.connect(ctx.destination);
  osc.start(at); osc.stop(at + dur + 0.03);
}

/** Played when a note is placed on the timeline. */
export function playPlaceSound(): void {
  if (!enabled || placeVariant === 'none') return;
  const lvl = volume * placeVol;
  if (lvl <= 0) return;
  // Reset the climbing pitch ramp if the user paused placing for a while.
  const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  if (now - lastPlaceAt > PLACE_PITCH_IDLE_RESET_MS) placePitchStep = 0;
  lastPlaceAt = now;
  // Pitch multiplier for this placement, then advance + wrap the ramp.
  const pitch = 1 + PLACE_PITCH_STEP * placePitchStep;
  placePitchStep = (placePitchStep + 1) % PLACE_PITCH_STEPS;
  try {
    const ctx = getContext();
    const t = ctx.currentTime;
    if (placeVariant === 'blip') {
      tone(ctx, t, 'square', 880 * pitch, 1320 * pitch, lvl * 0.3, 0.09);
    } else if (placeVariant === 'thock') {
      tone(ctx, t, 'sine', 300 * pitch, 130 * pitch, lvl * 0.55, 0.13);
    } else if (placeVariant === 'quack') {
      quack(ctx, t, lvl * 0.5, pitch);
    } else { // 'pop' — bright rising pluck
      tone(ctx, t, 'triangle', 440 * pitch, 920 * pitch, lvl * 0.42, 0.1);
    }
  } catch { /* audio blocked */ }
}

/** Played when a note is deleted from the timeline. */
export function playDeleteSound(): void {
  if (!enabled || deleteVariant === 'none') return;
  const lvl = volume * deleteVol;
  if (lvl <= 0) return;
  try {
    const ctx = getContext();
    const t = ctx.currentTime;
    if (deleteVariant === 'zap') {
      tone(ctx, t, 'sawtooth', 1200, 120, lvl * 0.32, 0.2);
    } else if (deleteVariant === 'thud') {
      tone(ctx, t, 'sine', 180, 55, lvl * 0.6, 0.2);
      emitNoise(ctx, t, 0.06, lvl * 0.2, 'lowpass', 1200, 400);
    } else { // 'shatter' — glassy noise crackle + descending ping
      emitNoise(ctx, t, 0.22, lvl * 0.35, 'bandpass', 4200, 800);
      tone(ctx, t, 'triangle', 1600, 480, lvl * 0.22, 0.2);
    }
  } catch { /* audio blocked */ }
}

export function setFlowAudioEnabled(on: boolean): void { enabled = on; }
export function setFlowAudioVolume(v: number): void { volume = Math.max(0, Math.min(1, v)); }
